import { useReveal } from "../../hooks/useReveal";

interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface ProcessSectionProps {
  title?: string;
  subtitle?: string;
  steps?: ProcessStep[];
}

const defaultSteps: ProcessStep[] = [
  {
    number: "01",
    title: "Consultation",
    description: "We begin with a personal conversation to understand your vision, timeline, wardrobe preferences, and the details that matter most to you.",
    icon: "ri-chat-heart-line",
  },
  {
    number: "02",
    title: "Your Session",
    description: "Arrive and be guided through a relaxed, beautifully orchestrated experience. Stephanie's calming presence ensures everyone feels confident and at ease.",
    icon: "ri-camera-line",
  },
  {
    number: "03",
    title: "Fine Art Editing",
    description: "Every image is meticulously edited to Precious Keepsakes' signature aesthetic — timeless, warm, and rich with emotion.",
    icon: "ri-magic-line",
  },
  {
    number: "04",
    title: "Heirloom Delivery",
    description: "Receive your private gallery and select premium print products — fine art prints, albums, and keepsakes designed to last generations.",
    icon: "ri-gift-line",
  },
];

export default function ProcessSection({
  title = "Your Experience, Step by Step",
  subtitle = "A stress-free journey from first hello to forever keepsakes",
  steps = defaultSteps,
}: ProcessSectionProps) {
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-pk-ivory py-28 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-8">
        {/* Header */}
        <div className="reveal text-center mb-20">
          <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">How It Works</span>
          <h2 className="font-display text-5xl md:text-6xl font-medium text-pk-charcoal mt-4 leading-tight">
            {title}
          </h2>
          <p className="font-sans text-sm text-pk-muted mt-4 max-w-lg mx-auto leading-7">{subtitle}</p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-pk-light rounded-sm overflow-hidden">
          {steps.map((step, i) => (
            <div
              key={i}
              className="reveal bg-pk-cream p-8 md:p-10 group hover:bg-pk-charcoal transition-colors duration-500 flex flex-col"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Number + icon row */}
              <div className="flex items-start justify-between mb-8">
                <span className="font-display text-6xl font-light text-pk-gold/20 group-hover:text-pk-gold/40 transition-colors duration-500 leading-none">
                  {step.number}
                </span>
                <div className="w-11 h-11 flex items-center justify-center border border-pk-light group-hover:border-pk-gold/40 rounded-sm transition-colors duration-500">
                  <i className={`${step.icon} text-pk-gold text-xl`} />
                </div>
              </div>

              {/* Title */}
              <h3 className="font-display text-2xl font-semibold text-pk-charcoal group-hover:text-white transition-colors duration-500 mb-3">
                {step.title}
              </h3>

              {/* Description */}
              <p className="font-sans text-sm text-pk-muted group-hover:text-white/60 transition-colors duration-500 leading-6 flex-1">
                {step.description}
              </p>

              {/* Bottom line accent */}
              <div className="mt-8 w-0 group-hover:w-12 h-px bg-pk-gold transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
